/**
 * Auth0 Guide-style Ask AI drawer — opens over the current page instead of
 * routing to a dedicated screen. Reuses `@authe/ai-assistant` for the chat
 * transport; the intro/onboarding chrome is dashboard-owned.
 */
import * as React from "react";
import { ChevronDown, ExternalLink, ShieldCheck, Sparkles } from "lucide-react";
import {
  AssistantComposer,
  AssistantMessage,
  useAssistant,
} from "@authe/ai-assistant";
import { useOptionalSession } from "@authe/ui-core/session";
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  cn,
} from "@authe/ui-core";
import { APP_NAME, EXTERNAL_LINKS } from "@/config";
import {
  ASK_AI_SUGGESTIONS,
  useAskAiActionHandler,
  useAskAiTransport,
} from "@/features/ask-ai/useAskAiAssistant";

export interface AskAiDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EXAMPLE_PROMPTS = ASK_AI_SUGGESTIONS.slice(0, 2);

function firstName(name: string | null | undefined): string {
  if (!name) return "there";
  return name.trim().split(/\s+/)[0] ?? "there";
}

export function AskAiDrawer({ open, onOpenChange }: AskAiDrawerProps): React.ReactElement {
  const transport = useAskAiTransport();
  const onAction = useAskAiActionHandler();
  const session = useOptionalSession()?.session ?? null;
  const displayName = firstName(session?.user?.name);
  const user = session?.user;

  const userAvatar = React.useMemo(
    () => ({
      src: user?.avatar ?? undefined,
      alt: user?.name ?? "User",
      fallback: (user?.name ?? user?.email ?? "U").slice(0, 1).toUpperCase(),
    }),
    [user?.avatar, user?.email, user?.name],
  );

  const { messages, status, error, input, setInput, send, stop } = useAssistant({
    transport,
    onAction,
  });

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const isEmpty = messages.length === 0;

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[420px]">
        {/* Accessible name/description for screen readers (Radix Dialog requires a
            title); the visible header below is purely presentational. */}
        <SheetTitle className="sr-only">{APP_NAME} Guide — Ask AI</SheetTitle>
        <SheetDescription className="sr-only">
          Ask questions about using and setting up {APP_NAME}, powered by AI.
        </SheetDescription>
        <div className="flex items-center gap-2 border-b border-border px-4 py-3 pr-12">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-brand/10 text-brand">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold">Guide</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium uppercase tracking-wide">
            Early
          </Badge>
        </div>

        <ScrollArea className="flex-1">
          <div ref={scrollRef} className="flex flex-col">
            {isEmpty ? (
              <div className="space-y-4 border-b border-border px-4 py-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Guide by {APP_NAME} AI
                  </p>
                  <h2 className="text-base font-semibold leading-snug">
                    Get answers to your {APP_NAME} questions
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    You can ask technical questions, such as &ldquo;how can I trace funds across
                    chains?&rdquo; or &ldquo;which transactions are high risk this week?&rdquo;
                  </p>
                </div>

                <GuideSection title="How Guide works">
                  We use {APP_NAME} documentation and your tenant data to find answers to your
                  questions.
                </GuideSection>

                <GuideSection title="Guide AI safeguards" icon={<ShieldCheck className="h-3.5 w-3.5" />}>
                  Your data is safeguarded by our security and compliance standards and is not used
                  for third-party AI model training.
                </GuideSection>

                <a
                  href={EXTERNAL_LINKS.docs}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  Learn more
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            ) : null}

            <div className="space-y-4 px-4 py-4">
              {isEmpty ? (
                <div className="space-y-3">
                  <p className="text-sm">
                    Hello {displayName}, ask me about how to use or set up {APP_NAME} features. For
                    example&hellip;
                  </p>
                  <div className="space-y-2">
                    {EXAMPLE_PROMPTS.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        disabled={status === "streaming"}
                        onClick={() => void send(prompt)}
                        className={cn(
                          "w-full rounded-md border border-border bg-card px-3 py-2.5 text-left text-sm",
                          "transition-colors hover:bg-accent/50 disabled:opacity-50",
                        )}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <AssistantMessage
                    key={message.id}
                    message={message}
                    onAction={onAction}
                    actionsDisabled={status === "streaming"}
                    userAvatar={userAvatar}
                  />
                ))
              )}

              {error && !messages.some((m) => m.error) ? (
                <p className="text-center text-xs text-destructive">{error.message}</p>
              ) : null}
            </div>
          </div>
        </ScrollArea>

        <div className="mt-auto border-t border-border">
          <AssistantComposer
            value={input}
            onChange={setInput}
            onSend={() => void send()}
            onStop={stop}
            streaming={status === "streaming"}
            placeholder="Ask me a question"
            className="border-t-0"
          />
          <p className="px-4 pb-3 text-[10px] leading-relaxed text-muted-foreground">
            By using this feature, our{" "}
            <a
              href={EXTERNAL_LINKS.privacy}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Privacy Policy
            </a>{" "}
            applies.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function GuideSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 text-left">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium">
          {icon}
          {title}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 text-sm text-muted-foreground">{children}</CollapsibleContent>
    </Collapsible>
  );
}
